import React from 'react';
import PropTypes from 'prop-types';
import {FormattedMessage} from 'react-intl';
import {Field} from 'redux-form';
import {Link} from 'react-router-dom';
import noFeeIcon from '../../assets/images/noFee.png';

import {
  ButtonText,
  ChoosePaymentOptionRow,
  CompleteOrderBtn,
  FormError,
  OptionLabel,
  PaymentDisclaimerLogosRow,
  PaymentDisclaimerRow,
  PaymentOptionsRow,
  PaymentOptionsTitle,
  PaymentsContainer,
  TextInputError,
  NoFee,
  ProviderWrapper,
  ProviderBy,
  WebsiteBy,
  PaymentLogo,
  OptionsWrapper, ErrorReport, CompleteOrderWrapper, Underline
} from './CheckoutPage.style';
import {ButtonSpinner} from '../../styles/components/button.style';
import {FooditLogo} from '../../components/Footer/Footer.style';
import {PaymentIcons} from '../../components/PaymentIcons/PaymentIcons';
import paymentLogo from '../../assets/images/globalPayments.png';

import RenderRadioButton from './RenderRadioButton';
import {FOODITWEBSITE, SERVICE_TYPE} from '../../constants/constant';
import {validateRequired} from '../../helpers/formValidation.helper';

const Payments = ({
    disableSubmitButton,
    formError,
    greySubmitButton,
    handleFormErrors,
    isCashActive,
    isCreditCardActive,
    isPaypalActive,
    isValidating,
    placeOrderButtonText,
    serviceTypeValue,
    setPayment,
    showInvalidFormData,
    showPaymentTypeRequiredError,
    showNoFee,
    checkoutForm,
    basketContainsErrorMessage,
    isDelivery,
    submitting,
    redirecting,
    isTemplateLight,
    orderFailed,
    handleErrorReportForm,
    restaurantCode,
    navUrls
}) => {
  const renderNoFeeIcon = showNoFee && (
    <NoFee
      src={noFeeIcon}
      alt="No card fee"
    />
  );
  const renderCreditCardOption = isCreditCardActive && (
    <div>
      <Field
        id="paymentCreditCard"
        type="radio"
        name="paymentType"
        value="CREDIT_CARD"
        component={RenderRadioButton}
        validate={[validateRequired]}
        callback={setPayment}
      />
      <OptionLabel htmlFor="paymentCreditCard">
        <FormattedMessage id={`checkout.paymentTypeCreditCard${restaurantCode === 'jjcafe' ? '.JJ' : ''}`} />
      </OptionLabel>
    </div>
  );

  const renderPaypalOption = isPaypalActive && (
    <div>
      <Field
        id="paymentPaypal"
        type="radio"
        name="paymentType"
        value="PAYPAL"
        component={RenderRadioButton}
        validate={[validateRequired]}
        callback={setPayment}
      />
      <OptionLabel htmlFor="paymentPaypal">
        <FormattedMessage id="checkout.paymentTypePayPal" />
      </OptionLabel>
    </div>
  );

  const serviceTypeOptionText = serviceTypeValue && (
    <FormattedMessage id={serviceTypeValue === SERVICE_TYPE.DELIVERY ? 'checkout.delivery' : 'checkout.collection'} />
  );

  const renderCashOption = isCashActive && (
    <div>
      <Field
        id="paymentCash"
        type="radio"
        name="paymentType"
        value="CASH"
        component={RenderRadioButton}
        validate={[validateRequired]}
        callback={setPayment}
      />
      <OptionLabel htmlFor="paymentCash">
        <FormattedMessage id={`checkout.paymentTypeCash${restaurantCode === 'jjcafe' ? '.JJ' : ''}`} />
        {serviceTypeOptionText}
      </OptionLabel>
    </div>
  );

  const renderPaymentTypeRequiredError = showPaymentTypeRequiredError && (
    <TextInputError>
      <FormattedMessage id="form.paymentType.required" />
    </TextInputError>
  );

  const renderOrderButtonText = (
    <ButtonText>
      <FormattedMessage id={placeOrderButtonText} />
    </ButtonText>
  );
  const renderOrderButtonSpinner = (
    <ButtonSpinner
      data-rw="checkout-page--complete-order-button--spinner"
      greySpinner={greySubmitButton || disableSubmitButton}
    />
  );
  const renderOrderButton = (isValidating || submitting || redirecting) ? renderOrderButtonSpinner : renderOrderButtonText;

  const renderInvalidFormData = (showInvalidFormData || basketContainsErrorMessage) && (
    <FormError>
      <FormattedMessage id="checkout.checkInvalidData" />
    </FormError>
  );

  const renderFormErrors = formError && (
    <FormError>
      <FormattedMessage id={formError} />
    </FormError>
  );

  const renderAsyncErrors =
    checkoutForm &&
    checkoutForm.syncErrors &&
    Object.entries(checkoutForm.syncErrors).map(error => {
      if (
        !isDelivery &&
        (error[0] === 'addressLine1' || error[0] === 'city' || error[0] === 'postcode')
      ) {
        return;
      }
      return (
        <TextInputError key={error}>
          <FormattedMessage id={error[1] === 'required' ? `form.${error[0]}.required` : error[1]} />
        </TextInputError>
      );
    });

  const renderPoweredBy = !isTemplateLight && (
    <ProviderWrapper>
      <ProviderBy>
        <FormattedMessage id="checkout.websiteBy" />
      </ProviderBy>
      <WebsiteBy href={FOODITWEBSITE.URL}>
        <FooditLogo
          src="https://cdn-dot-foodit-prod.appspot.com/assets/foodit--logo.svg"
          alt="FOODit"
        />
      </WebsiteBy>
    </ProviderWrapper>
  );

  const renderErrorReportFormLink = orderFailed && (
    <ErrorReport onClick={handleErrorReportForm}>
      <FormattedMessage id="checkout.troubleOrdering" />
      <Underline><FormattedMessage id="checkout.reportAnError" /></Underline>
    </ErrorReport>
  );

  return (
    <PaymentsContainer>
      <PaymentOptionsRow id="checkoutPaymentType">
        <PaymentOptionsTitle>
          <FormattedMessage id="checkout.paymentOptions" />
        </PaymentOptionsTitle>
        <PaymentIcons
          supportCreditCard={isCreditCardActive}
          supportPayPal={isPaypalActive}
        />
      </PaymentOptionsRow>
      <ChoosePaymentOptionRow>
        <OptionsWrapper>
          <div>
            {renderCreditCardOption}
            {renderPaypalOption}
            {renderCashOption}
            {renderPaymentTypeRequiredError}
          </div>
          {renderNoFeeIcon}
        </OptionsWrapper>
        <div>
          <CompleteOrderWrapper>
            <CompleteOrderBtn
              type="submit"
              greyed={greySubmitButton}
              disabled={disableSubmitButton}
              data-rw="checkout-page--complete-order-button"
              onClick={handleFormErrors}
            >
              {renderOrderButton}
            </CompleteOrderBtn>
            {renderErrorReportFormLink}
          </CompleteOrderWrapper>
          {renderInvalidFormData}
          {renderAsyncErrors}
          {renderFormErrors}
        </div>
      </ChoosePaymentOptionRow>
      <PaymentDisclaimerRow isTemplateLight={isTemplateLight}>
        <p>
          <FormattedMessage
            id="checkout.paymentDisclaimer"
            values={{
              link: (
                <Link
                  to={navUrls.termsUrl}
                  target="_blank"
                >
                  <FormattedMessage id="checkout.termsAndConditions" />
                </Link>
              )
            }}
          />
        </p>
        <PaymentDisclaimerLogosRow>
          {renderPoweredBy}
          <ProviderWrapper>
            <ProviderBy>
              <FormattedMessage id="checkout.poweredBy" />
            </ProviderBy>
            <PaymentLogo
              src={paymentLogo}
              alt="Powered by Global Payments"
            />
          </ProviderWrapper>
        </PaymentDisclaimerLogosRow>
      </PaymentDisclaimerRow>
    </PaymentsContainer>
  );
};

Payments.propTypes = {
  disableSubmitButton: PropTypes.bool.isRequired,
  formError: PropTypes.object,
  greySubmitButton: PropTypes.bool.isRequired,
  handleFormErrors: PropTypes.func.isRequired,
  isCashActive: PropTypes.bool.isRequired,
  isCreditCardActive: PropTypes.bool.isRequired,
  isPaypalActive: PropTypes.bool.isRequired,
  isValidating: PropTypes.bool.isRequired,
  placeOrderButtonText: PropTypes.string.isRequired,
  serviceTypeValue: PropTypes.func.isRequired,
  setPayment: PropTypes.func.isRequired,
  showInvalidFormData: PropTypes.bool.isRequired,
  showPaymentTypeRequiredError: PropTypes.bool.isRequired,
  checkoutForm: PropTypes.object,
  showNoFee: PropTypes.bool.isRequired,
  basketContainsErrorMessage: PropTypes.bool,
  isDelivery: PropTypes.bool.required,
  submitting: PropTypes.bool.required,
  redirecting: PropTypes.bool.required,
  isTemplateLight: PropTypes.bool.required,
  orderFailed: PropTypes.bool.required,
  handleErrorReportForm: PropTypes.func.required,
  restaurantCode: PropTypes.number.isRequired,
  navUrls: PropTypes.object
};

export default Payments;



// WEBPACK FOOTER //
// ./containers/CheckoutPage/Payments.js